import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * De mail-triage (T3.5).
 *
 * ── LEZEN GAAT VIA RLS, SCHRIJVEN VIA RPC'S ──────────────────────────────────
 *
 * Berichten en suggesties komen met een gewone `select` binnen; migratie 035
 * geeft `crm_user` en `admin` leesrecht binnen hun eigen tenant.
 *
 * Goedkeuren, afwijzen en overslaan gaan **uitsluitend** via drie
 * `SECURITY DEFINER`-RPC's. Die doen de routering naar notities, taken,
 * documenten, contacten en leads én schrijven de audittrail (`approved_by`,
 * `reviewed_by`). Schrijf hier dus nooit rechtstreeks naar `email_suggestions`
 * of `email_inbox` — dan mis je de helft.
 *
 * **Wie mag beslissen staat op één plek: `mail_beslis_check()` in de database**
 * (migratie 043, verruimd in 045). Sinds T3.11 zijn dat `super_admin`, `admin`
 * en `crm_user`, elk alleen binnen de eigen tenant. `useMagTriageren()`
 * hieronder spiegelt die lijst zodat de knoppen uit staan in plaats van een
 * fout te geven — maar de database is de autoriteit, niet deze hook.
 *
 * ── DE TABELLEN ZIJN LEEG TOT DE INGEST-WORKER DRAAIT ────────────────────────
 *
 * `email_inbox` telt nul rijen. Het scherm is dus niet stuk als het leeg is; er
 * is nog geen post opgehaald (T3.3). Zeg dat in de UI ook zo, anders lijkt een
 * werkend scherm kapot.
 */

export type EmailClassification =
  // 'unclassified' = opgehaald maar nog niet beoordeeld (migratie 037). Alle
  // andere waarden zijn een oordeel over de inhoud; deze is het ontbreken ervan.
  | 'unclassified'
  | 'skip' | 'note' | 'todo' | 'document' | 'status_update' | 'contact_update' | 'lead';

export type EmailProcessingStatus =
  | 'pending' | 'suggestions_ready' | 'approved' | 'rejected' | 'skipped' | 'partial';

export type SuggestionStatus = 'pending' | 'approved' | 'rejected' | 'edited';

export type EmailSuggestion = {
  id: string;
  email_inbox_id: string;
  suggestion_type: Exclude<EmailClassification, 'skip' | 'unclassified'>;
  target_table: string;
  target_entity_id: string | null;
  target_entity_name: string | null;
  suggested_data: Record<string, unknown>;
  ai_reasoning: string | null;
  confidence: number | null;
  status: SuggestionStatus;
  created_at: string;
};

export type EmailMessage = {
  id: string;
  message_id: string;
  from_email: string | null;
  from_name: string | null;
  to_emails: string[] | null;
  subject: string | null;
  body_preview: string | null;
  received_at: string | null;
  has_attachments: boolean | null;
  attachment_names: string[] | null;
  classification: EmailClassification;
  classification_confidence: number | null;
  processing_status: EmailProcessingStatus;
  created_at: string;
  email_suggestions: EmailSuggestion[];
};

/** Berichten die nog een beslissing nodig hebben, nieuwste eerst. */
export function useEmailTriage(status: EmailProcessingStatus[] = ['pending', 'suggestions_ready', 'partial']) {
  return useQuery({
    queryKey: ['email-triage', status],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_inbox')
        .select('*, email_suggestions(*)')
        .in('processing_status', status)
        .order('received_at', { ascending: false, nullsFirst: false })
        .limit(200);

      if (error) throw error;
      return (data ?? []) as unknown as EmailMessage[];
    },
  });
}

/** Tellers per status, voor de tabbladen. */
export function useEmailTriageCounts() {
  return useQuery({
    queryKey: ['email-triage-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_inbox')
        .select('processing_status');
      if (error) throw error;

      const tellers: Record<string, number> = {};
      for (const rij of data ?? []) {
        const s = (rij as { processing_status: string }).processing_status;
        tellers[s] = (tellers[s] ?? 0) + 1;
      }
      return tellers;
    },
  });
}

/**
 * Mag de ingelogde gebruiker beslissingen nemen?
 *
 * De RPC's weigeren het anders toch, maar een knop die een onbegrijpelijke fout
 * geeft is erger dan een knop die uitlegt waarom hij uit staat.
 */
export function useMagTriageren() {
  return useQuery({
    queryKey: ['mag-triageren'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;
      // Zelfde drie rollen als mail_beslis_check(). Wijkt dit af, dan staat de
      // knop aan terwijl de RPC weigert (of andersom) — houd ze gelijk.
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .in('role', ['super_admin', 'admin', 'crm_user'])
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return Boolean(data);
    },
    staleTime: 5 * 60 * 1000,
  });
}

function useTriageMutation<TArgs>(
  fn: (args: TArgs) => Promise<void>,
  succesTekst: string
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-triage'] });
      queryClient.invalidateQueries({ queryKey: ['email-triage-counts'] });
      toast.success(succesTekst);
    },
    onError: (error: any) => {
      // mail_beslis_check() levert al een Nederlandse reden ("Dit bericht hoort
      // bij een andere organisatie."); die tonen we zoals hij is. Alleen de
      // oude Engelse melding uit functies die nog niet om zijn, vertalen we.
      const rauw = String(error?.message ?? error);
      toast.error(
        rauw.includes('Unauthorized')
          ? 'Je mag deze beslissing niet nemen.'
          : rauw
      );
    },
  });
}

/** Goedkeuren; `edited` overschrijft de voorgestelde inhoud. */
export function useApproveSuggestion() {
  return useTriageMutation<{ suggestionId: string; edited?: Record<string, unknown> | null }>(
    async ({ suggestionId, edited }) => {
      const { error } = await supabase.rpc('approve_email_suggestion', {
        p_suggestion_id: suggestionId,
        p_edited_data: (edited ?? null) as any,
      });
      if (error) throw error;
    },
    'Suggestie goedgekeurd'
  );
}

export function useRejectSuggestion() {
  return useTriageMutation<{ suggestionId: string }>(
    async ({ suggestionId }) => {
      const { error } = await supabase.rpc('reject_email_suggestion', {
        p_suggestion_id: suggestionId,
      });
      if (error) throw error;
    },
    'Suggestie afgewezen'
  );
}

/** Hele bericht overslaan: het bericht op 'skipped', alle suggesties afgewezen. */
export function useSkipEmail() {
  return useTriageMutation<{ emailId: string }>(
    async ({ emailId }) => {
      const { error } = await supabase.rpc('skip_email', { p_email_inbox_id: emailId });
      if (error) throw error;
    },
    'Bericht overgeslagen'
  );
}

/**
 * Post ophalen (T3.3). Roept de `mail-ingest`-function aan voor de ingelogde
 * gebruiker; een cron doet hetzelfde voor iedereen met een gekoppelde postbus.
 *
 * Faalt dit met een melding over opnieuw koppelen, dan is dat geen storing maar
 * een actie: `Mail.Read` is nieuw sinds T3.2, dus iedereen moet zijn
 * Microsoft-account eenmalig opnieuw koppelen.
 */
export function useMailOphalen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('mail-ingest', { body: {} });
      if (error) throw error;
      if (data && data.ok === false) throw new Error(data.fout ?? 'Ophalen mislukt');
      return data as { nieuw?: number; gebruikers?: number };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['email-triage'] });
      queryClient.invalidateQueries({ queryKey: ['email-triage-counts'] });
      const nieuw = data?.nieuw ?? 0;
      toast.success(
        nieuw === 0
          ? 'Geen nieuwe post gevonden'
          : `${nieuw} nieuw bericht${nieuw === 1 ? '' : 'en'} opgehaald`
      );
    },
    onError: (error: any) => toast.error(String(error?.message ?? error)),
  });
}

/**
 * Classificeren (T3.4). `dryrun` roept het model niet aan maar rekent alleen uit
 * wat een echte run zou kosten - draai dat eerst.
 */
export function useMailClassificeren() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (opties?: { dryrun?: boolean }) => {
      const { data, error } = await supabase.functions.invoke('mail-classify', {
        body: { dryrun: opties?.dryrun ?? false },
      });
      if (error) throw error;
      if (data && data.ok === false) throw new Error(data.fout ?? 'Classificeren mislukt');
      return data as {
        droogloop?: boolean; draden_totaal?: number; modelaanroepen?: number;
        geschatte_tokens_in?: number; suggesties?: number;
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['email-triage'] });
      queryClient.invalidateQueries({ queryKey: ['email-triage-counts'] });
      toast.success(
        data?.droogloop
          ? `Droogloop: ${data.draden_totaal ?? 0} draden, naar schatting ${(data.geschatte_tokens_in ?? 0).toLocaleString('nl-NL')} tokens. Er is niets aangeroepen.`
          : `${data?.suggesties ?? 0} suggesties uit ${data?.modelaanroepen ?? 0} draden`
      );
    },
    onError: (error: any) => toast.error(String(error?.message ?? error)),
  });
}

/** Schoongemaakte tekst van een bericht (migratie 038), voor de koppeldialoog. */
export function useMailTekst(emailId: string | null) {
  return useQuery({
    queryKey: ['mail-tekst', emailId],
    enabled: Boolean(emailId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('mail_teksten_voor', { p_ids: [emailId] });
      if (error) throw error;
      return (data?.[0]?.tekst ?? null) as string | null;
    },
    staleTime: Infinity,
  });
}

const NOTITIE_TABEL: Record<'project' | 'kavel' | 'opportunity', string> = {
  project: 'project_notes',
  kavel: 'charging_location_notes',
  opportunity: 'opportunity_notes',
};

const MATCH_KOLOM: Record<'project' | 'kavel' | 'opportunity', string> = {
  project: 'matched_project_id',
  kavel: 'matched_location_id',
  opportunity: 'matched_opportunity_id',
};

const SCOPE: Record<'project' | 'kavel' | 'opportunity', string> = {
  project: 'project',
  kavel: 'location',
  opportunity: 'opportunity',
};

/**
 * Een bericht met de hand koppelen: maakt een suggestie en keurt hem meteen goed.
 *
 * Bewust via de RPC en niet rechtstreeks naar `project_notes` of `unified_todos`.
 * Zo loopt handmatig koppelen door dezelfde routering en audittrail als een
 * goedgekeurde suggestie van de classifier. Een tweede schrijfpad zou betekenen
 * dat twee routes hetzelfde doen maar net niet hetzelfde vastleggen.
 *
 * De todo-tak van de RPC leest `matched_*` van het BERICHT, niet van de
 * suggestie — dus die kolom moet eerst gezet zijn.
 */
export function useKoppelBericht() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      emailId: string;
      soort: 'note' | 'todo';
      dossierSoort: 'project' | 'kavel' | 'opportunity';
      dossierId: string;
      titel: string;
      tekst: string;
      termijn: string | null;
    }) => {
      const { error: matchFout } = await supabase
        .from('email_inbox')
        .update({
          [MATCH_KOLOM[args.dossierSoort]]: args.dossierId,
          classification: args.soort === 'todo' ? 'todo' : 'note',
          classification_confidence: 1.0,
        } as any)
        .eq('id', args.emailId);
      if (matchFout) throw matchFout;

      const suggested_data =
        args.soort === 'todo'
          ? {
              title: args.titel,
              description: args.tekst,
              priority: 'medium',
              due_date: args.termijn,
              scope: SCOPE[args.dossierSoort],
              is_shared: true,
            }
          : {
              title: args.titel,
              body: args.tekst || '(geen tekst)',
              // Verplicht expliciet: de default 'email' van de RPC is ongeldig
              // voor project_notes. Zie ticket T3.9.
              note_type: 'note',
            };

      const { data: suggestie, error: maakFout } = await supabase
        .from('email_suggestions')
        .insert({
          email_inbox_id: args.emailId,
          suggestion_type: args.soort,
          target_table: args.soort === 'todo' ? 'unified_todos' : NOTITIE_TABEL[args.dossierSoort],
          target_entity_id: args.soort === 'todo' ? null : args.dossierId,
          target_entity_name: null,
          suggested_data,
          ai_reasoning: 'Handmatig gekoppeld.',
          confidence: 1.0,
          status: 'pending',
        } as any)
        .select('id')
        .single();
      if (maakFout) throw maakFout;

      const { error: keurFout } = await supabase.rpc('approve_email_suggestion', {
        p_suggestion_id: (suggestie as any).id,
        p_edited_data: null,
      });
      if (keurFout) throw keurFout;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-triage'] });
      queryClient.invalidateQueries({ queryKey: ['email-triage-counts'] });
      toast.success('Gekoppeld');
    },
    onError: (error: any) => {
      const rauw = String(error?.message ?? error);
      toast.error(
        rauw.includes('Unauthorized')
          ? 'Alleen een super-admin kan post aan een dossier koppelen.'
          : rauw
      );
    },
  });
}

/**
 * Bijlagen naar SharePoint (T3.6, uitgebreid met T3.10).
 *
 * Werkt voor alle drie de dossiersoorten. Projecten hebben hun map al; voor een
 * kavel of opportunity wordt hij aangemaakt op het moment dat er een bijlage
 * heen moet — er is bewust geen inrichtingsronde die ruim honderd lege mappen
 * achterlaat. Berichten zonder dossier worden overgeslagen met de reden erbij.
 */
export function useBijlagenNaarSharePoint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('mail-attachments', { body: {} });
      if (error) throw error;
      if (data && data.ok === false) throw new Error(data.fout ?? 'Verplaatsen mislukt');
      return data as {
        behandeld?: number; gelukt?: number; resterend?: number;
        resultaten?: Array<{ bijlage: string; overgeslagen?: string; fout?: string; url?: string }>;
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['email-triage'] });
      const gelukt = data?.gelukt ?? 0;
      const overgeslagen = (data?.resultaten ?? []).filter((r) => r.overgeslagen).length;
      const mislukt = (data?.resultaten ?? []).filter((r) => r.fout).length;

      if (gelukt === 0 && overgeslagen > 0) {
        // Geen fout, maar ook geen resultaat - zeg waarom, anders lijkt het stuk.
        toast.info(
          `Niets verplaatst. ${overgeslagen} bijlage${overgeslagen === 1 ? '' : 'n'} overgeslagen: ` +
          `koppel het bericht eerst aan een dossier.`
        );
      } else {
        toast.success(
          `${gelukt} bijlage${gelukt === 1 ? '' : 'n'} naar SharePoint` +
          (overgeslagen ? `, ${overgeslagen} overgeslagen` : '') +
          (mislukt ? `, ${mislukt} mislukt` : '')
        );
      }
      if (mislukt) console.warn('[bijlagen] mislukt:', data?.resultaten?.filter((r) => r.fout));
    },
    onError: (error: any) => toast.error(String(error?.message ?? error)),
  });
}
